import React, { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isIOS } from "react-device-detect";
import { UserModel } from "../apis/axiosTypes";
import { ACCESS_TOKEN } from "../utils/constants";
import { WebChatApi } from "../apis/webChat.api";
import { useChatClient } from "../hooks/useChatClient";
import { Channel as ChannelType } from "stream-chat/dist/types/channel";
import { DefaultGenerics } from "stream-chat";
import { datadogRum } from "@datadog/browser-rum";

type WebChatContextType = {
  isAuthenticated?: boolean | null;
  onLogOut: () => void;
  fetchMe: (callback?: any) => Promise<void>;
  setIosInstructions: React.Dispatch<React.SetStateAction<boolean>>;
  setNotificationPermission: React.Dispatch<
    React.SetStateAction<NotificationPermission | undefined>
  >;
  iosInstructions: boolean;
  notificationPermission: NotificationPermission | undefined;
  isShowIosInstructions: () => void;
  getFCMToken: () => string;
  userModel: UserModel | undefined;
  setFCMToken: (value: string) => void;
  setChannel: (channel: ChannelType<DefaultGenerics> | undefined) => void;
  channel: ChannelType<DefaultGenerics> | undefined;
  handleCreateChannel: () => Promise<ChannelType<DefaultGenerics> | undefined>;
};

const WebChatContext = React.createContext<WebChatContextType>({
  onLogOut: () => {},
  fetchMe: async () => {},
  setIosInstructions: () => {},
  setNotificationPermission: () => {},
  iosInstructions: false,
  notificationPermission: undefined,
  isShowIosInstructions: () => {},
  getFCMToken: () => "",
  userModel: undefined,
  setFCMToken: (value: string) => {},
  setChannel: (value: ChannelType<DefaultGenerics> | undefined) => {},
  channel: undefined,
  handleCreateChannel: async () => {
    return undefined;
  },
});

type WebChatProviderProps = {
  children: ReactNode | ReactNode[];
};

function WebChatProvider(props: WebChatProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [iosInstructions, setIosInstructions] = useState(false);
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>();
  const [userModel, setUserModel] = useState<UserModel>();
  const [channel, setChannel] = useState<ChannelType<DefaultGenerics>>();

  const isShowIosInstructions = () => {
    if (!("Notification" in window)) {
      if (isIOS) {
        setIosInstructions(true);
      }
    } else {
      setNotificationPermission(Notification.permission);
    }
  };

  const navigate = useNavigate();
  const { setupClient, chatClient } = useChatClient();

  const dataDogInit = () => {
    datadogRum.init({
      applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID ?? "",
      clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN ?? "",
      // `site` refers to the Datadog site parameter of your organization
      // see https://docs.datadoghq.com/getting_started/site/
      site: "datadoghq.com",
      service: "chat-web",
      env: process.env.REACT_APP_DATADOG_ENV,
      // Specify a version number to identify the deployed version of your application in Datadog
      // version: '1.0.0',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
    });
  };

  useEffect(() => {
    if (
      process.env.REACT_APP_DATADOG_APPLICATION_ID &&
      process.env.REACT_APP_DATADOG_CLIENT_TOKEN &&
      process.env.REACT_APP_DATADOG_ENV
    ) {
      dataDogInit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    process.env.REACT_APP_DATADOG_APPLICATION_ID,
    process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
    process.env.REACT_APP_DATADOG_ENV,
  ]);

  useEffect(() => {
    fetchMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogOut = () => {
    navigate("/otp-login");
    localStorage.removeItem(ACCESS_TOKEN);
    // localStorage.removeItem(FCM_TOKEN);
    setUserModel(undefined);
    setIsAuthenticated(false);
    // deleteFCMToken();
  };

  const fetchMe = async (callback?: () => null) => {
    const token = localStorage.getItem(ACCESS_TOKEN);
    isShowIosInstructions();

    if (token) {
      const user = WebChatApi.getMe();
      user.then((response) => {
        if (response) {
          setupClient(response?.guid, response?.name, response?.authToken);
          setUserModel(response);
          datadogRum.setUser({ id: response.userId });
        }
      });
    }

    setIsAuthenticated(!!token);
  };

  const getFCMToken = () => {
    // return localStorage.getItem(FCM_TOKEN) ?? "";
    return "";
  };

  const setFCMToken = (token: string) => {
    // localStorage.setItem(FCM_TOKEN, token);
  };

  const handleCreateChannel = async () => {
    try {
      const channelData = await WebChatApi.getNewChannelId();
      const newChannel = chatClient.channel("system-jarvis", channelData.id);
      await newChannel.watch();
      setChannel(newChannel);
      return newChannel;
    } catch (error) {
      console.error("Error creating channel:", error);
    } finally {
    }
  };

  return (
    <WebChatContext.Provider
      value={{
        onLogOut,
        isAuthenticated,
        fetchMe,
        setIosInstructions,
        setNotificationPermission,
        iosInstructions,
        notificationPermission,
        isShowIosInstructions,
        getFCMToken,
        userModel,
        setFCMToken,
        setChannel,
        channel,
        handleCreateChannel,
      }}
      {...props}
    />
  );
}

function useWebChat() {
  const context = React.useContext(WebChatContext);

  if (context === undefined) {
    throw new Error("useWebChat must be used within a WebChatProvider");
  }
  return context;
}

export { WebChatProvider, useWebChat };
